import { Component } from '@theme/component'
import { debounce, isClickedOutside, onAnimationEnd } from '@theme/utilities'

let activeBodyScrollLocks = 0
let lockedScrollY = 0

const previousBodyStyles = {
  width: '',
  position: '',
  top: '',
  left: '',
  right: ''
}

/**
 * Hard upper bound for the close-out animation. Any longer than this and we
 * assume the animation never started (e.g. CSS not applied) and force the
 * dialog closed so it never gets stuck half-open.
 */
const CLOSE_ANIMATION_TIMEOUT_MS = 350

/**
 * @param {HTMLDialogElement} dialog
 * @returns {boolean}
 */
export function dialogIsModalTopLayer(dialog) {
  if (!(dialog instanceof HTMLDialogElement)) return false
  if (typeof dialog.matches !== 'function') return false

  try {
    return dialog.matches(':modal')
  } catch {
    return false
  }
}

/** @type {boolean | null} */
let modalPseudoClassSupported = null

/**
 * Whether `:modal` can be used in selector APIs (`matches`, `closest`, etc.).
 * Safari 15.4–15.5 throws SyntaxError for `:modal` (added in 15.6).
 *
 * @returns {boolean}
 */
function supportsModalPseudoClass() {
  if (modalPseudoClassSupported !== null) return modalPseudoClassSupported

  if (typeof HTMLDialogElement === 'undefined') {
    modalPseudoClassSupported = false
    return false
  }

  try {
    document.createElement('dialog').matches(':modal')
    modalPseudoClassSupported = true
  } catch {
    modalPseudoClassSupported = false
  }

  return modalPseudoClassSupported
}

/**
 * Nearest open modal dialog ancestor, equivalent to `element.closest('dialog:modal')`
 * without throwing when `:modal` is unsupported.
 *
 * @param {EventTarget | null} node
 * @returns {HTMLDialogElement | null}
 */
export function findModalDialogAncestor(node) {
  if (!(node instanceof Element)) return null

  let current = node
  while (current) {
    if (current instanceof HTMLDialogElement && current.open) {
      if (dialogIsModalTopLayer(current)) return current

      if (
        !supportsModalPseudoClass() &&
        current.classList.contains('dialog-modal')
      ) {
        return current
      }
    }

    current = current.parentElement
  }

  return null
}

/**
 * Ensures a dialog is in the modal top layer without touching scroll-lock
 * counters. Section Rendering morph can leave attribute-only `open`, which
 * breaks `:modal` layout while scroll lock remains active.
 *
 * @param {HTMLDialogElement} dialog
 * @returns {boolean}
 */
export function ensureDialogModalPresentation(dialog) {
  if (!(dialog instanceof HTMLDialogElement)) return false

  if (typeof dialog.showModal !== 'function') {
    if (!dialog.open) dialog.setAttribute('open', '')
    return dialog.open
  }

  if (dialog.open && dialogIsModalTopLayer(dialog)) return true

  if (dialog.open && !dialogIsModalTopLayer(dialog)) {
    try {
      if (typeof dialog.close === 'function') {
        dialog.close()
      }
    } catch {
      /* ignore */
    }

    try {
      dialog.removeAttribute('open')
    } catch {
      /* ignore */
    }
  }

  try {
    dialog.showModal()
    return dialogIsModalTopLayer(dialog) || dialog.open
  } catch (error) {
    const err = /** @type {Error} */ (error)
    if (err.name === 'InvalidStateError' && dialogIsModalTopLayer(dialog)) {
      return true
    }

    try {
      if (typeof dialog.close === 'function') dialog.close()
    } catch {
      /* ignore */
    }

    try {
      dialog.removeAttribute('open')
      dialog.showModal()
      return dialogIsModalTopLayer(dialog) || dialog.open
    } catch {
      dialog.setAttribute('open', '')
      return dialog.open
    }
  }
}

function hasOpenScrollLockDetails() {
  return Boolean(document.querySelector('details[scroll-lock][open]'))
}

export class ScrollLockChangeEvent extends CustomEvent {
  /**
   * @param {boolean} locked
   */
  constructor(locked) {
    super(ScrollLockChangeEvent.eventName, {
      bubbles: true,
      detail: { locked }
    })
  }

  static eventName = 'theme:scroll-lock-change'
}

/**
 * Whether the page uses body-fixed scroll lock (window.scrollY reads as 0).
 *
 * @returns {boolean}
 */
export function isPageScrollLocked() {
  return activeBodyScrollLocks > 0
}

/**
 * Effective vertical scroll position for UI that must stay stable while menus are open.
 *
 * @returns {number}
 */
export function getPageScrollY() {
  if (activeBodyScrollLocks > 0) return lockedScrollY

  return (
    window.scrollY ||
    document.documentElement.scrollTop ||
    document.body.scrollTop ||
    0
  )
}

/**
 * Reloads and back/forward loads can restore a scroll offset before any
 * `scroll` listener is attached; fresh navigations always start at the top
 * unless they target a hash.
 *
 * @returns {boolean}
 */
export function navigationMayRestoreScroll() {
  const entry = performance.getEntriesByType?.('navigation')?.[0]
  const type = entry && 'type' in entry ? entry.type : ''
  return type === 'reload' || type === 'back_forward'
}

/**
 * Width of the classic scrollbar when the page is scrollable.
 *
 * @returns {number}
 */
export function getScrollbarWidth() {
  return Math.max(0, window.innerWidth - document.documentElement.clientWidth)
}

/** @returns {boolean} */
function scrollbarGutterIsStable() {
  const scrollbarGutter = getComputedStyle(
    document.documentElement
  ).scrollbarGutter
  if (!scrollbarGutter) return false
  return scrollbarGutter.split(/\s+/).includes('stable')
}

/** @param {number} scrollbarWidth */
function applyScrollLockPadding(scrollbarWidth) {
  if (scrollbarWidth <= 0 || scrollbarGutterIsStable()) {
    clearScrollLockPadding()
    return
  }

  document.documentElement.style.setProperty(
    '--scroll-lock-padding-inline-end',
    `${scrollbarWidth}px`
  )
}

function clearScrollLockPadding() {
  document.documentElement.style.removeProperty(
    '--scroll-lock-padding-inline-end'
  )
}

/** @param {boolean} locked */
function dispatchScrollLockChange(locked) {
  document.dispatchEvent(new ScrollLockChangeEvent(locked))
}

function lockBodyScroll() {
  const wasLocked = activeBodyScrollLocks > 0
  activeBodyScrollLocks += 1

  if (activeBodyScrollLocks > 1) return

  lockedScrollY = window.scrollY
  previousBodyStyles.width = document.body.style.width
  previousBodyStyles.position = document.body.style.position
  previousBodyStyles.top = document.body.style.top
  previousBodyStyles.left = document.body.style.left
  previousBodyStyles.right = document.body.style.right

  const scrollbarWidth = getScrollbarWidth()
  applyScrollLockPadding(scrollbarWidth)

  // Let scroll-linked UI snapshot scroll position and inline transforms while
  // CSS view-timeline animations are still active; then disable them via
  // html[scroll-lock] without a one-frame snap to translateX(0).
  if (!wasLocked) dispatchScrollLockChange(true)

  document.documentElement.setAttribute('scroll-lock', '')

  document.body.style.width = '100%'
  document.body.style.position = 'fixed'
  document.body.style.top = `-${lockedScrollY}px`
  document.body.style.left = '0'
  document.body.style.right = '0'
}

function unlockBodyScroll() {
  const bodyWasFixed = document.body.style.position === 'fixed'

  if (activeBodyScrollLocks <= 0 && !bodyWasFixed) return

  if (activeBodyScrollLocks > 0) {
    activeBodyScrollLocks -= 1
  }

  if (activeBodyScrollLocks > 0) return

  const restoreY = bodyWasFixed
    ? lockedScrollY || Math.abs(Number.parseFloat(document.body.style.top)) || 0
    : lockedScrollY
  const html = document.documentElement
  const previousScrollBehavior = html.style.scrollBehavior

  clearScrollLockPadding()

  if (!hasOpenScrollLockDetails()) {
    html.removeAttribute('scroll-lock')
  }

  if (!bodyWasFixed) {
    dispatchScrollLockChange(false)
    return
  }

  html.style.scrollBehavior = 'auto'

  document.body.style.width = previousBodyStyles.width
  document.body.style.position = previousBodyStyles.position
  document.body.style.top = previousBodyStyles.top
  document.body.style.left = previousBodyStyles.left
  document.body.style.right = previousBodyStyles.right

  window.scrollTo(0, restoreY)
  html.scrollTop = restoreY

  html.style.scrollBehavior = previousScrollBehavior

  dispatchScrollLockChange(false)
}

/** Locks background scroll (dialogs, mega menu). */
export function lockPageScroll() {
  lockBodyScroll()
}

/** Releases a lock acquired by {@link lockPageScroll}. */
export function unlockPageScroll() {
  unlockBodyScroll()
}

/**
 * A custom element that manages a dialog.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog – The dialog element.
 *
 * @extends Component<Refs>
 */
export class DialogComponent extends Component {
  requiredRefs = ['dialog']

  /**
   * Element that had focus before the dialog opened. Restored on close so
   * keyboard / screen reader users return to the activator (e.g. cart icon).
   *
   * @type {HTMLElement | null}
   */
  #previouslyFocused = null

  /**
   * Re-entrancy guard: set to true while a close-out animation is in flight
   * so rapid double-clicks (close button + Escape, etc.) don't queue
   * overlapping close passes.
   *
   * @type {boolean}
   */
  #closing = false

  #getDialogElement() {
    const directChildDialog = Array.from(this.children).find(
      (child) =>
        child instanceof HTMLDialogElement &&
        child.getAttribute('ref') === 'dialog'
    )

    if (directChildDialog instanceof HTMLDialogElement) {
      return directChildDialog
    }

    return this.refs.dialog instanceof HTMLDialogElement
      ? this.refs.dialog
      : null
  }

  #resetBodyScrollLock() {
    const bodyFixed = document.body.style.position === 'fixed'
    if (!isPageScrollLocked() && !bodyFixed) return
    unlockBodyScroll()
  }

  connectedCallback() {
    super.connectedCallback()

    if (this.minWidth || this.maxWidth) {
      window.addEventListener('resize', this.#handleResize)
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback()
    if (this.minWidth || this.maxWidth) {
      window.removeEventListener('resize', this.#handleResize)
    }
  }

  #handleResize = debounce(() => {
    const { minWidth, maxWidth } = this

    if (!minWidth && !maxWidth) return

    const windowWidth = window.innerWidth
    if (windowWidth < minWidth || windowWidth > maxWidth) {
      this.closeDialog()
    }
  }, 50)

  /**
   * Hook for subclasses to run cleanup just before the close animation
   * starts. Default is a no-op. Subclasses MUST keep this synchronous and
   * side-effect-only (DOM cleanup, abort signals) — the close pipeline
   * does not await it.
   *
   * @protected
   */
  onBeforeClose() {}

  /**
   * Hook for subclasses to react to a successful open after the dialog has
   * entered the top layer.
   *
   * @protected
   */
  onAfterOpen() {}

  /**
   * Hook for subclasses to react after the dialog has fully closed.
   *
   * @protected
   */
  onAfterClose() {}

  /**
   * Whether a close animation is in flight.
   *
   * @protected
   * @returns {boolean}
   */
  get isClosing() {
    return this.#closing
  }

  /**
   * Clears an in-flight close animation without unlocking scroll or firing
   * close hooks. Used when Section Rendering morph leaves a stale
   * `dialog-closing` class on an still-open drawer.
   *
   * @protected
   */
  clearClosingState() {
    this.#closing = false
    const dialog = this.#getDialogElement()
    dialog?.classList.remove('dialog-closing')
  }

  /**
   * Ensures the dialog is presented with `showModal()` so `:modal` layout and
   * the top layer stay active. Attribute-only `open` after morph is not enough.
   *
   * @protected
   * @returns {boolean}
   */
  ensureModalTopLayer() {
    const dialog = this.#getDialogElement()
    if (!(dialog instanceof HTMLDialogElement)) return false

    return ensureDialogModalPresentation(dialog)
  }

  /**
   * Re-enters the dialog top layer when native `open` was dropped but the
   * drawer should remain visible. Does not increment scroll lock.
   *
   * @protected
   * @returns {boolean} Whether the dialog reports open after the attempt
   */
  reenterTopLayerIfNeeded() {
    const dialog = this.#getDialogElement()
    if (!(dialog instanceof HTMLDialogElement)) return false
    if (dialog.open && dialogIsModalTopLayer(dialog)) return true

    return this.ensureModalTopLayer()
  }

  /**
   * Re-attaches modal dismiss listeners after morph when the dialog stayed
   * open but listener wiring may have been lost.
   *
   * @protected
   */
  ensureModalInteractionListeners() {
    if (this.#closing) return

    const dialog = this.#getDialogElement()
    if (!(dialog instanceof HTMLDialogElement) || !dialog.open) return

    this.removeEventListener('click', this.#handleClick)
    this.removeEventListener('keydown', this.#handleKeyDown)
    this.addEventListener('click', this.#handleClick)
    this.addEventListener('keydown', this.#handleKeyDown)
  }

  /**
   * Drops orphaned body scroll lock when this dialog is fully closed.
   *
   * @protected
   */
  releaseOrphanScrollLock() {
    if (this.#closing) return

    const dialog = this.#getDialogElement()
    if (
      dialog instanceof HTMLDialogElement &&
      dialog.open &&
      dialogIsModalTopLayer(dialog)
    ) {
      return
    }

    if (this.dataset.dialogOpen) {
      delete this.dataset.dialogOpen
    }

    this.#resetBodyScrollLock()
  }

  /**
   * Shows the dialog.
   */
  showDialog() {
    const dialog = this.#getDialogElement()
    if (!(dialog instanceof HTMLDialogElement)) return

    if (dialog.open) return

    // Capture the activator BEFORE entering the modal so we can restore
    // focus to it on close (a11y).
    const activeElement = document.activeElement
    if (
      activeElement instanceof HTMLElement &&
      activeElement !== document.body &&
      !this.contains(activeElement)
    ) {
      this.#previouslyFocused = activeElement
    }

    lockBodyScroll()

    // Present the modal on the next frame; scroll lock runs synchronously so
    // the page does not paint one frame at the pre-lock scroll position.
    requestAnimationFrame(() => {
      try {
        if (typeof dialog.showModal === 'function') {
          dialog.showModal()
        } else {
          dialog.setAttribute('open', '')
        }

        this.dataset.dialogOpen = 'true'
        this.dispatchEvent(new DialogOpenEvent())
        this.addEventListener('click', this.#handleClick)
        this.addEventListener('keydown', this.#handleKeyDown)
        this.onAfterOpen()
      } catch (error) {
        this.#resetBodyScrollLock()

        if (window.Shopify?.designMode) {
          console.error('[theme] Failed to open dialog', error)
        }
      }
    })
  }

  /**
   * Closes the dialog.
   */
  closeDialog = async () => {
    const dialog = this.#getDialogElement()
    if (!(dialog instanceof HTMLDialogElement)) return

    if (!dialog.open) {
      // Drawer can lose native `open` during Section Rendering morph while
      // scroll lock + data-dialog-open remain — recover without re-entering
      // the close animation pipeline.
      if (this.dataset.dialogOpen || this.#closing) {
        this.#closing = false
        dialog.classList.remove('dialog-closing')
        this.removeEventListener('click', this.#handleClick)
        this.removeEventListener('keydown', this.#handleKeyDown)
        this.#resetBodyScrollLock()
        delete this.dataset.dialogOpen
        this.onAfterClose()
        this.dispatchEvent(new DialogCloseEvent())
      }
      return
    }
    if (this.#closing) return
    this.#closing = true

    this.removeEventListener('click', this.#handleClick)
    this.removeEventListener('keydown', this.#handleKeyDown)

    try {
      this.onBeforeClose()
    } catch (error) {
      if (window.Shopify?.designMode) {
        console.error('[theme] onBeforeClose threw', error)
      }
    }

    dialog.classList.add('dialog-closing')

    try {
      // Race the animation against a hard timeout so a missing animation
      // (e.g. user CSS override, tests, reduced motion edge cases) cannot
      // leave the dialog stuck in the closing state.
      await Promise.race([
        onAnimationEnd(dialog, undefined, { subtree: false }),
        new Promise((resolve) =>
          setTimeout(resolve, CLOSE_ANIMATION_TIMEOUT_MS)
        )
      ])
    } catch (error) {
      if (window.Shopify?.designMode) {
        console.error('[theme] Failed to close dialog cleanly', error)
      }
    } finally {
      this.#resetBodyScrollLock()

      if (typeof dialog.close === 'function') {
        dialog.close()
      } else {
        dialog.removeAttribute('open')
      }

      dialog.classList.remove('dialog-closing')
      delete this.dataset.dialogOpen

      // Restore focus to whatever was focused before we opened, but only
      // if it's still in the document (it might have been morphed away).
      const previouslyFocused = this.#previouslyFocused
      this.#previouslyFocused = null
      if (previouslyFocused?.isConnected) {
        try {
          previouslyFocused.focus({ preventScroll: true })
        } catch {
          // Some elements throw on .focus() in jsdom / odd states; ignore.
        }
      }

      this.#closing = false
      this.onAfterClose()
    }

    this.dispatchEvent(new DialogCloseEvent())
  }

  /**
   * Toggles the dialog.
   */
  toggleDialog = () => {
    if (this.refs.dialog.open) {
      this.closeDialog()
    } else {
      this.showDialog()
    }
  }

  /**
   * Closes the dialog when the user clicks outside of it.
   *
   * @param {MouseEvent} event - The mouse event.
   */
  #handleClick(event) {
    const { dialog } = this.refs

    if (isClickedOutside(event, dialog)) {
      this.closeDialog()
    }
  }

  /**
   * Closes the dialog when the user presses the escape key.
   *
   * @param {KeyboardEvent} event - The keyboard event.
   */
  #handleKeyDown(event) {
    if (event.key !== 'Escape') return

    event.preventDefault()
    this.closeDialog()
  }

  /**
   * Gets the minimum width of the dialog.
   *
   * @returns {number} The minimum width of the dialog.
   */
  get minWidth() {
    return Number(this.getAttribute('dialog-active-min-width'))
  }

  /**
   * Gets the maximum width of the dialog.
   *
   * @returns {number} The maximum width of the dialog.
   */
  get maxWidth() {
    return Number(this.getAttribute('dialog-active-max-width'))
  }
}

if (!customElements.get('dialog-component'))
  customElements.define('dialog-component', DialogComponent)

export class DialogOpenEvent extends CustomEvent {
  constructor() {
    super(DialogOpenEvent.eventName)
  }

  static eventName = 'dialog:open'
}

export class DialogCloseEvent extends CustomEvent {
  constructor() {
    super(DialogCloseEvent.eventName)
  }

  static eventName = 'dialog:close'
}

document.addEventListener(
  'toggle',
  (event) => {
    if (event.target instanceof HTMLDetailsElement) {
      if (event.target.hasAttribute('scroll-lock')) {
        const { open } = event.target

        if (open) {
          document.documentElement.setAttribute('scroll-lock', '')
        } else {
          if (activeBodyScrollLocks === 0) {
            document.documentElement.removeAttribute('scroll-lock')
          }
        }
      }
    }
  },
  { capture: true }
)
